import React, {lazy, Suspense} from 'react';
import {Route, Routes} from 'react-router-dom';
import {ThemeProvider} from '@gravity-ui/uikit';
import {
    AuthWrapper,
    Login,
    LoginWithToken,
    PersonalAccountLayout,
    RemindPass,
    Wrapper
} from '@components';
import {AppContext, AppState, loadStateFromLS} from '@app/context';
import {createEmitter} from '@app/events';
import {CustomToasterProvider} from 'app/Providers';
import {ErrorPage} from '@pages/ErrorPage';
import {CookieConsentToastGate} from '@app/Providers/CustomToasterProvider/CookieConsentToastGate';
import {KeyboardNavigationProvider} from '@components/KeyboardNavigation';
import {ErrorBoundary} from '@components/ErrorBoundary';

const BalancePage = lazy(() =>
    import('@pages/BalancePage').then((m) => ({default: m.BalancePage}))
);
const BasketPage = lazy(() => import('@pages/BasketPage').then((m) => ({default: m.BasketPage})));
// const CatalogPage = lazy(() =>
//     import('@pages/CatalogPage').then((m) => ({default: m.CatalogPage}))
// );
const ConditionRefundPage = lazy(() =>
    import('@pages/ConditionRefundPage').then((m) => ({default: m.ConditionRefundPage}))
);
const ContactsPage = lazy(() =>
    import('@pages/ContactsPage').then((m) => ({default: m.ContactsPage}))
);
const HomePage = lazy(() => import('@pages/HomePage').then((m) => ({default: m.HomePage})));
const IndexPage = lazy(() => import('@pages/IndexPage').then((m) => ({default: m.IndexPage})));
const LevelPricePage = lazy(() =>
    import('@pages/LevelPricePage').then((m) => ({default: m.LevelPricePage}))
);
const FavoritePriceListsPage = lazy(() =>
    import('@pages/FavoritePriceListsPage').then((m) => ({default: m.FavoritePriceListsPage}))
);
const OrdersPage = lazy(() => import('@pages/OrdersPage').then((m) => ({default: m.OrdersPage})));
const PersonInfoPage = lazy(() =>
    import('@pages/PersonInfoPage').then((m) => ({default: m.PersonInfoPage}))
);
const ProfilePage = lazy(() =>
    import('@pages/ProfilePage').then((m) => ({default: m.ProfilePage}))
);
const SearchPage = lazy(() => import('@pages/SearchPage').then((m) => ({default: m.SearchPage})));
const UserAddressPage = lazy(() =>
    import('@pages/UserAddressPage').then((m) => ({default: m.UserAddressPage}))
);
const OnePage = lazy(() => import('@pages/OnePage').then((m) => ({default: m.OnePage})));
const OfferPage = lazy(() => import('@pages/OfferPage').then((m) => ({default: m.OfferPage})));

const App = () => {
    const [state, setState] = React.useState<AppState>(loadStateFromLS());

    return (
        <AppContext.Provider value={{state: state, emit: createEmitter(setState)}}>
            <ThemeProvider theme="light">
                <ErrorBoundary>
                    <CustomToasterProvider>
                        <CookieConsentToastGate />
                        <Suspense fallback={null}>
                            <KeyboardNavigationProvider>
                                <Routes>
                                    <Route path="/" element={<Wrapper />}>
                                        <Route path="" index element={<IndexPage />} />
                                        <Route path="/one" element={<OnePage />} />
                                        <Route path="/login" element={<Login />}></Route>
                                        <Route path="/remind-pass" element={<RemindPass />}></Route>
                                        <Route
                                            path="/login-with-token/:token"
                                            element={<LoginWithToken />}
                                        />
                                        {/* <Route path="*"></Route> */}
                                    </Route>

                                    <Route element={<AuthWrapper />}>
                                        <Route element={<PersonalAccountLayout />}>
                                            <Route path="/home" element={<HomePage />} />
                                            <Route path="/search" element={<SearchPage />} />
                                            <Route path="/basket" element={<BasketPage />} />
                                            <Route path="/orders" element={<OrdersPage />}>
                                                <Route path="returns" element={null} />
                                            </Route>
                                            <Route path="/profile" element={<ProfilePage />}>
                                                <Route index element={<PersonInfoPage />} />
                                                <Route
                                                    path="person-info"
                                                    index
                                                    element={<PersonInfoPage />}
                                                />
                                                <Route
                                                    path="user-address"
                                                    element={<UserAddressPage />}
                                                />
                                                <Route
                                                    path="price-level"
                                                    element={<LevelPricePage />}
                                                />
                                                <Route
                                                    path="favorite-prices"
                                                    element={<FavoritePriceListsPage />}
                                                />
                                                <Route path="contacts" element={<ContactsPage />} />
                                                <Route
                                                    path="condition-refund"
                                                    element={<ConditionRefundPage />}
                                                />
                                                <Route path="offer" element={<OfferPage />} />
                                            </Route>
                                            {/* <Route path="/catalog" element={<CatalogPage />} /> */}
                                            <Route path="/balance" element={<BalancePage />}>
                                                <Route path="payment-schedule" element={null} />
                                                <Route path="document-request" element={null} />
                                                <Route path="document-flow" element={null} />
                                            </Route>
                                            <Route path="/error" element={<ErrorPage />} />
                                            <Route
                                                path="*"
                                                element={<ErrorPage errorCode={404} />}
                                            />
                                        </Route>
                                    </Route>
                                </Routes>
                            </KeyboardNavigationProvider>
                        </Suspense>
                    </CustomToasterProvider>
                </ErrorBoundary>
            </ThemeProvider>
        </AppContext.Provider>
    );
};

export default App;
